// Pure view-model helpers for the thread-based chat. Kept as total,
// network-free functions (mirroring the codebase's `operatorFeed.ts` /
// `supervisorEventTime.ts` convention) so the mapping + sort + reconcile logic is
// unit-tested without rendering or a live backend, and the view layer stays
// declarative.
//
// The chat surface renders two real shapes from `api/client`:
//   - `Thread`      → a thread-list row (kind label + title + unread).
//   - `ChatMessage` → a line in the open thread, mapped onto the shared
//                     `Message` view shape the MessageList already renders.
//
// Sender mapping: a message's `sender_kind` + `sender_email` place it in one
// of the MessageList lanes:
//   - the caller's own email   → "you"     (right-indented, no spine)
//   - sender_kind "orchestrator" (another human) → "person"
//   - anything else (agents/operators) → "person" too, named by its kind —
//     these threads have no operator-accent source, so the honest render is
//     a plain named participant line. (The operator ACCENT belongs to the
//     operator activity feed, kept separate from chat.)

import type { ChatMessage, Thread } from "../api/client";
import type { Message } from "@waytide/ui";
import { formatSupervisorEventWhen } from "../dashboard/supervisorEventTime";

// ─── Control messages (Phase 9 pause-and-play) ───────────────────────
// A pause/resume posts a `sender_kind: "control"` message into the thread
// whose body is one of these exact tokens. The raw token must NEVER reach the
// chat as text — it's mapped to a calm system-style line by `controlLabel`,
// and the latest control message derives whether the session is paused. These
// are kept as pure, total helpers (mirroring the rest of this module) so the
// derivation + label mapping are unit-tested without rendering.

/** The server-stamped sender_kind for a pause/resume control message. */
export const CONTROL_SENDER_KIND = "control";

/** The two control-message body tokens (exact, as the backend posts them). */
export const HALT_TOKEN = "::waytide-halt::";
export const RESUME_TOKEN = "::waytide-resume::";

/** True when a message is a pause/resume control message (by sender_kind). */
export function isControlMessage(m: ChatMessage): boolean {
  return m.sender_kind === CONTROL_SENDER_KIND;
}

/**
 * Map a control-message body TOKEN to its calm, human-readable label. A halt
 * reads as a pause; a resume reads as a resume; an unknown control body falls
 * back to a neutral "agent control" line so an unrecognised token still never
 * leaks its raw form into the chat. Pure + total.
 */
export function controlLabel(body: string): string {
  switch (body.trim()) {
    case HALT_TOKEN:
      return "⏸ You paused this agent";
    case RESUME_TOKEN:
      return "▶ You resumed this agent";
    default:
      return "agent control";
  }
}

/**
 * Derive whether an agent session is currently paused from its message list:
 * the LATEST control message wins — a halt means paused, a resume (or no
 * control message at all) means running. Order-independent (resolves by the
 * same time key `buildChatMessages` uses, ties broken by original order so the
 * later-listed control message wins on a tie). Pure + total.
 */
export function isSessionPaused(messages: ChatMessage[]): boolean {
  const controls = messages
    .map((m, i) => ({ m, i, at: messageSortKey(m) }))
    .filter(({ m }) => isControlMessage(m));
  if (controls.length === 0) return false;
  // Latest wins: by time, ties broken toward the later-listed control message.
  const latest = controls.reduce((best, cur) =>
    cur.at > best.at || (cur.at === best.at && cur.i >= best.i) ? cur : best,
  );
  return latest.m.body.trim() === HALT_TOKEN;
}

/** Human-readable label for a thread kind, shown on the roster row. */
export function threadKindLabel(kind: Thread["kind"]): string {
  switch (kind) {
    case "dm":
      return "DM";
    case "group":
      return "group";
    case "agent_session":
      return "agent session";
    default:
      return kind;
  }
}

/** A thread-list row: the thread plus its derived display bits. `unread` is
 * driven by the live stream (a message arriving for a non-open thread). */
export interface ThreadRow {
  thread: Thread;
  kindLabel: string;
  /** Relative time of the thread's last known activity. */
  when: string;
  unread: boolean;
}

/**
 * Build the thread-list rows from the caller's threads + the set of thread
 * ids currently marked unread. Threads are returned newest-first by
 * `created_at` (defensively re-sorted so an out-of-order input still renders
 * newest-first, matching `listThreads`' documented order).
 */
export function threadRows(
  threads: Thread[],
  unreadIds: ReadonlySet<number>,
  now: number = Date.now(),
): ThreadRow[] {
  return [...threads]
    .sort((a, b) => sortKey(b) - sortKey(a))
    .map((thread) => ({
      thread,
      kindLabel: threadKindLabel(thread.kind),
      when: formatSupervisorEventWhen(thread.created_at, "", now),
      unread: unreadIds.has(thread.id),
    }));
}

/** Comparable ms key for a thread (created_at is micros; 0 when absent). */
function sortKey(t: Thread): number {
  return Number.isFinite(t.created_at) && t.created_at > 0
    ? Math.floor(t.created_at / 1000)
    : 0;
}

/** Comparable ms key for a chat message (created_at is micros). */
function messageSortKey(m: ChatMessage): number {
  return Number.isFinite(m.created_at) && m.created_at > 0
    ? Math.floor(m.created_at / 1000)
    : 0;
}

/** A short display name for a non-you sender: the local-part of the email,
 * or the sender_kind when the email is empty. */
export function senderLabel(m: ChatMessage): string {
  const email = m.sender_email.trim();
  if (email) {
    const at = email.indexOf("@");
    return at > 0 ? email.slice(0, at) : email;
  }
  return m.sender_kind.trim() || "participant";
}

/**
 * Map one `ChatMessage` to its view `Message`. `callerEmail` decides the
 * "you" lane (case-insensitive on the email). Everyone else is a plain named
 * "person" line — chat threads carry no operator accent.
 */
export function viewMessageFromChat(
  m: ChatMessage,
  callerEmail: string,
  now: number,
): Message {
  const when = formatSupervisorEventWhen(m.created_at, "", now);
  // A pause/resume control message renders as a calm system-style line — its
  // raw `::waytide-halt::`/`::waytide-resume::` token is mapped to a label and
  // NEVER shown verbatim in the chat.
  if (isControlMessage(m)) {
    return {
      id: `cm-${m.id}`,
      from: "system",
      text: controlLabel(m.body),
      when,
    };
  }
  const isYou =
    callerEmail.trim() !== "" &&
    m.sender_email.trim().toLowerCase() === callerEmail.trim().toLowerCase();
  if (isYou) {
    return { id: `cm-${m.id}`, from: "you", author: "you", text: m.body, when };
  }
  return {
    id: `cm-${m.id}`,
    from: "person",
    author: senderLabel(m),
    text: m.body,
    when,
  };
}

/**
 * Build the open thread's message list, OLDEST→NEWEST (newest at the bottom,
 * the composer pinned beneath — the thread convention). The input may arrive
 * in any order (and may include an optimistically-appended local row); we
 * sort by the resolved time key, ties stable on original order.
 */
export function buildChatMessages(
  messages: ChatMessage[],
  callerEmail: string,
  now: number = Date.now(),
): Message[] {
  return [...messages]
    .map((m, i) => ({ m, i, at: messageSortKey(m) }))
    .sort((a, b) => (a.at !== b.at ? a.at - b.at : a.i - b.i))
    .map(({ m }) => viewMessageFromChat(m, callerEmail, now));
}

/**
 * Merge an incoming live message into a thread's cached list, de-duped on id
 * so the SSE echo of a message we optimistically appended (or already loaded)
 * doesn't double-render. Returns the same array reference when nothing
 * changes, so callers can skip a needless state update.
 */
export function mergeIncomingMessage(
  existing: ChatMessage[],
  incoming: ChatMessage,
): ChatMessage[] {
  if (existing.some((m) => m.id === incoming.id)) return existing;
  return [...existing, incoming];
}

/**
 * Merge an incoming live thread into the caller's thread list, de-duped on
 * id (a `thread` event may announce a brand-new thread OR re-announce an
 * existing one). A new thread is prepended (newest-first); an existing one is
 * replaced in place. Returns the same reference when the row is byte-identical
 * so callers can skip a needless update.
 */
export function mergeIncomingThread(
  existing: Thread[],
  incoming: Thread,
): Thread[] {
  const idx = existing.findIndex((t) => t.id === incoming.id);
  if (idx === -1) return [incoming, ...existing];
  const current = existing[idx];
  if (
    current.kind === incoming.kind &&
    current.title === incoming.title &&
    current.creator_email === incoming.creator_email &&
    current.created_at === incoming.created_at
  ) {
    return existing;
  }
  const next = existing.slice();
  next[idx] = incoming;
  return next;
}
